import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import api from '../lib/api';
import {
  Phone as PhoneIcon, EnvelopeSimple, VideoCamera, NotePencil,
  ListChecks, Bell, Check, ClockCounterClockwise, X, ArrowsClockwise, CalendarBlank,
  Sparkle, Spinner
} from '@phosphor-icons/react';
import { WatchHowLink } from './WatchHowLink';

const TYPE_ICONS = { call: PhoneIcon, email: EnvelopeSimple, meeting: VideoCamera, note: NotePencil, todo: ListChecks };
const PRIORITY_COLORS = { high: 'border-red-500', medium: 'border-amber-400', low: 'border-gray-300' };
const PRIORITY_BADGES = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-gray-100 text-gray-600',
};

const fmtDate = (isoDay) => {
  if (!isoDay) return '';
  const d = new Date(isoDay + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
const fmtTime = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};
const daysAgo = (isoDay) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(isoDay + 'T00:00:00');
  const diff = Math.round((today - d) / (1000 * 60 * 60 * 24));
  if (diff <= 0) return 'today';
  if (diff === 1) return '1 day late';
  return `${diff} days late`;
};

// Collect unique, ordered contact_ids from a list of alarm items.
// Preserves the on-screen order so Next/Prev in the contact card walks
// through alarms top-to-bottom.
const collectContactIds = (items) => {
  const seen = new Set();
  const ids = [];
  for (const a of items) {
    if (a.contact_id && !seen.has(a.contact_id)) {
      seen.add(a.contact_id);
      ids.push(a.contact_id);
    }
  }
  return ids;
};

export default function AlarmsModal({ user }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ overdue: [], today_items: [], overdue_count: 0, today_count: 0 });
  const [busy, setBusy] = useState(false);
  const [autoRolledCount, setAutoRolledCount] = useState(0);
  // Pre-call brief overlay — {contact_id, contact_name, loading, data, error}
  const [brief, setBrief] = useState(null);

  const viewBrief = async (a) => {
    setBrief({ contact_id: a.contact_id, contact_name: a.contact_name, loading: true, data: null });
    try {
      const { data } = await api.get(`/agents/brief/${a.contact_id}`);
      setBrief({ contact_id: a.contact_id, contact_name: a.contact_name, loading: false, data: data.brief });
    } catch {
      setBrief({ contact_id: a.contact_id, contact_name: a.contact_name, loading: false, data: null, error: true });
    }
  };

  // Drag state — ACT! 4.0 reminder windows could be repositioned. Default is
  // top-right so the rest of the app stays accessible behind it.
  const panelRef = useRef(null);
  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('alarms_panel_pos') || 'null');
      if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) return saved;
    } catch {}
    return null; // null => default top-right
  });
  const dragRef = useRef({ active: false, offsetX: 0, offsetY: 0 });

  const todayKey = new Date().toISOString().substring(0, 10);
  const seenKey = `alarms_seen_${user?.user_id}_${todayKey}`;

  const fetchAlarms = useCallback(async () => {
    try {
      const { data } = await api.get('/alarms');
      setData(data);
      return data;
    } catch (err) {
      console.error('Alarms fetch failed', err);
      return null;
    }
  }, []);

  // Auto-show once per day per user, after login — with automatic rollover.
  // ACT! 4.0 rule: only pop when something actually needs you now —
  // items were rolled over, something is overdue, or a timed item is due.
  useEffect(() => {
    if (!user?.user_id) return;
    if (localStorage.getItem(seenKey)) return;
    (async () => {
      let rolled = 0;
      try {
        const { data: roll } = await api.post('/alarms/rollover');
        rolled = roll?.rolled_over || 0;
        setAutoRolledCount(rolled);
      } catch (err) {
        console.error('Auto-rollover failed', err);
      }
      const result = await fetchAlarms();
      const now = new Date();
      const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const dueNow = (result?.today_items || []).some(a => a.scheduled_time && a.scheduled_time <= hhmm);
      if (result && (rolled > 0 || result.overdue_count > 0 || dueNow)) {
        setOpen(true);
      }
      localStorage.setItem(seenKey, '1');
    })();
  }, [user?.user_id, seenKey, fetchAlarms]);

  // Keyboard shortcut: Cmd/Ctrl+B opens alarms anytime; Esc closes.
  useEffect(() => {
    const onKey = async (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        await fetchAlarms();
        setOpen(true);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fetchAlarms, open]);

  // Drag handlers — pointer events for desktop + touch
  const onHeaderPointerDown = (e) => {
    if (!panelRef.current) return;
    // Don't initiate a drag from the close button itself
    if (e.target.closest('button')) return;
    const rect = panelRef.current.getBoundingClientRect();
    dragRef.current = {
      active: true,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
    e.target.setPointerCapture?.(e.pointerId);
  };
  const onHeaderPointerMove = (e) => {
    if (!dragRef.current.active) return;
    const x = e.clientX - dragRef.current.offsetX;
    const y = e.clientY - dragRef.current.offsetY;
    // Constrain to viewport
    const maxX = window.innerWidth - 320;
    const maxY = window.innerHeight - 80;
    setPos({
      x: Math.max(0, Math.min(maxX, x)),
      y: Math.max(0, Math.min(maxY, y)),
    });
  };
  const onHeaderPointerUp = () => {
    if (dragRef.current.active) {
      dragRef.current.active = false;
      if (pos) localStorage.setItem('alarms_panel_pos', JSON.stringify(pos));
    }
  };

  const completeOne = async (activityId) => {
    setBusy(true);
    try {
      await api.put(`/activities/${activityId}/complete`);
      setData(d => ({
        ...d,
        overdue: d.overdue.filter(a => a.activity_id !== activityId),
        today_items: d.today_items.filter(a => a.activity_id !== activityId),
        overdue_count: d.overdue.filter(a => a.activity_id !== activityId).length,
        today_count: d.today_items.filter(a => a.activity_id !== activityId).length,
      }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const snoozeOne = async (activityId) => {
    setBusy(true);
    try {
      await api.post(`/alarms/${activityId}/snooze`);
      setData(d => ({
        ...d,
        overdue: d.overdue.filter(a => a.activity_id !== activityId),
        today_items: d.today_items.filter(a => a.activity_id !== activityId),
        overdue_count: d.overdue.filter(a => a.activity_id !== activityId).length,
        today_count: d.today_items.filter(a => a.activity_id !== activityId).length,
      }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const rolloverAll = async () => {
    if (!data.overdue_count) return;
    setBusy(true);
    try {
      await api.post('/alarms/rollover');
      await fetchAlarms();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  // ACT! 4.0 behavior: clicking an alarm builds a Lookup of just that section
  // (overdue OR today) and navigates to the clicked contact. Prev/Next in the
  // contact card will then step through those alarms only.
  const goTo = (a, sectionItems, sectionLabel) => {
    setOpen(false);
    if (!a.contact_id) {
      navigate('/tasks');
      return;
    }
    const ids = collectContactIds(sectionItems);
    navigate(`/contact?id=${a.contact_id}`, {
      state: {
        lookupContactIds: ids,
        lookupLabel: sectionLabel,
        targetContactId: a.contact_id,
      },
    });
  };

  const renderItem = (a, isOverdue, sectionItems, sectionLabel) => {
    const Icon = TYPE_ICONS[a.activity_type] || NotePencil;
    return (
      <li
        key={a.activity_id}
        className={`group flex items-stretch border-l-4 ${PRIORITY_COLORS[a.priority] || 'border-gray-300'} bg-white border-y border-r border-[#E5E7EB] hover:bg-[#FFFBEB] transition-colors`}
        data-testid={`alarm-item-${a.activity_id}`}
      >
        <button
          onClick={() => goTo(a, sectionItems, sectionLabel)}
          className="flex-1 text-left px-3 py-2.5 min-w-0"
          data-testid={`alarm-open-${a.activity_id}`}
        >
          <div className="flex items-center gap-2">
            <Icon size={14} weight="bold" className="text-[#0B5394] shrink-0" />
            <span className="text-[13px] font-semibold text-[#111] truncate">{a.title}</span>
            <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${PRIORITY_BADGES[a.priority] || ''}`}>{a.priority}</span>
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-[11px] text-[#666]">
            <span className="flex items-center gap-1">
              <CalendarBlank size={10} />
              {fmtDate(a.scheduled_date)}
              {a.scheduled_time && ` · ${fmtTime(a.scheduled_time)}`}
            </span>
            {isOverdue && <span className="text-[#C0392B] font-semibold">{daysAgo(a.scheduled_date)}</span>}
            <span className="text-[#0B5394] truncate">
              {a.contact_name}{a.contact_company ? ` · ${a.contact_company}` : ''}
            </span>
          </div>
          {a.content && <p className="text-[11px] text-[#777] mt-1 line-clamp-1">{a.content}</p>}
        </button>
        <div className="flex items-center gap-1 px-2 border-l border-[#EEE]">
          {a.is_brief_reminder && a.contact_id && (
            <button
              disabled={busy || (brief?.loading && brief?.contact_id === a.contact_id)}
              onClick={() => viewBrief(a)}
              className="flex items-center gap-1 px-2 py-1 bg-[#F39C12] hover:bg-[#D68910] disabled:opacity-60 text-black text-[10px] font-bold rounded whitespace-nowrap"
              title="Crunchy's pre-call brief — WHO / HISTORY / OPENER"
              data-testid={`alarm-view-brief-${a.activity_id}`}
            >
              {brief?.loading && brief?.contact_id === a.contact_id
                ? <Spinner size={11} className="animate-spin" />
                : <Sparkle size={11} weight="fill" />}
              View Brief
            </button>
          )}
          <div className="flex items-center gap-1 opacity-50 group-hover:opacity-100 transition-opacity">
          <button
            disabled={busy}
            onClick={() => completeOne(a.activity_id)}
            className="p-1.5 hover:bg-green-100 text-green-700 rounded"
            title="Mark done"
            data-testid={`alarm-complete-${a.activity_id}`}
          >
            <Check size={14} weight="bold" />
          </button>
          <button
            disabled={busy}
            onClick={() => snoozeOne(a.activity_id)}
            className="p-1.5 hover:bg-amber-100 text-amber-700 rounded"
            title="Snooze to tomorrow"
            data-testid={`alarm-snooze-${a.activity_id}`}
          >
            <ClockCounterClockwise size={14} weight="bold" />
          </button>
          </div>
        </div>
      </li>
    );
  };

  if (!open) return null;

  // ACT!-style non-modal floating panel — no backdrop, no overlay.
  // Sits in the top-right by default but can be dragged anywhere.
  const positionStyle = pos
    ? { top: pos.y, left: pos.x }
    : { top: 64, right: 16 };

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Today's Alarms"
      className="fixed z-50 w-[480px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-96px)] flex flex-col bg-white border border-[#999] shadow-2xl rounded-sm"
      style={positionStyle}
      data-testid="alarms-dialog"
    >
      {/* Header — drag handle */}
      <div
        className="px-4 py-2.5 bg-gradient-to-b from-[#F39C12] to-[#D68910] border-b border-[#999] shrink-0 cursor-move select-none"
        onPointerDown={onHeaderPointerDown}
        onPointerMove={onHeaderPointerMove}
        onPointerUp={onHeaderPointerUp}
        onPointerCancel={onHeaderPointerUp}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="text-white text-[14px] font-bold flex items-center gap-2 min-w-0">
            <Bell size={16} weight="fill" className="shrink-0" />
            <span>Today's Alarms</span>
            <span className="text-[11px] font-normal opacity-90 ml-1 truncate">
              {data.overdue_count > 0 && `${data.overdue_count} overdue · `}
              {data.today_count} for today
            </span>
            <WatchHowLink slug="alarms" light className="ml-1 shrink-0" />
          </div>
          <button
            onClick={() => setOpen(false)}
            className="p-1 text-white/90 hover:bg-white/20 rounded shrink-0"
            title="Close (Esc)"
            data-testid="alarms-close-x"
            aria-label="Close alarms"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-[#F5F5F5] p-3 space-y-3">
        {/* Auto-rollover banner (ACT! 4.0 style) */}
        {autoRolledCount > 0 && (
          <div
            className="flex items-center gap-2 px-3 py-2 bg-[#FFF3D6] border border-[#F39C12] text-[#7A4F00] text-[12px]"
            data-testid="auto-rollover-banner"
          >
            <ArrowsClockwise size={13} weight="bold" className="shrink-0" />
            <span>
              <strong>{autoRolledCount}</strong> overdue {autoRolledCount === 1 ? 'item' : 'items'} rolled over to today. Complete or reschedule below.
            </span>
          </div>
        )}

        {/* OVERDUE */}
        {data.overdue.length > 0 && (
          <section data-testid="alarms-overdue-section">
            <div className="flex items-center justify-between mb-1.5 px-1">
              <h3 className="text-[11px] font-bold text-[#C0392B] uppercase tracking-wide">
                Overdue ({data.overdue.length})
              </h3>
              <button
                disabled={busy}
                onClick={rolloverAll}
                className="text-[11px] text-[#0B5394] hover:underline font-semibold flex items-center gap-1"
                data-testid="alarm-rollover-all"
                title="Push all overdue items to today"
              >
                <ArrowsClockwise size={11} weight="bold" />
                Roll all over to today
              </button>
            </div>
            <ul className="space-y-1">
              {data.overdue.map(a => renderItem(a, true, data.overdue, 'Overdue Alarms'))}
            </ul>
          </section>
        )}

        {/* TODAY */}
        {data.today_items.length > 0 && (
          <section data-testid="alarms-today-section">
            <h3 className="text-[11px] font-bold text-[#0B5394] uppercase tracking-wide mb-1.5 px-1">
              Today ({data.today_items.length})
            </h3>
            <ul className="space-y-1">
              {data.today_items.map(a => renderItem(a, false, data.today_items, "Today's Alarms"))}
            </ul>
          </section>
        )}

        {data.overdue_count === 0 && data.today_count === 0 && (
          <div className="text-center py-12 text-[#666]" data-testid="alarms-empty">
            <Check size={42} weight="bold" className="mx-auto text-green-500 mb-2" />
            <div className="text-[14px] font-semibold text-[#111]">All clear!</div>
            <div className="text-[12px] text-[#666] mt-0.5">Nothing on your plate for today.</div>
          </div>
        )}
      </div>

      <div className="px-4 py-2.5 bg-[#E0E0E0] border-t border-[#999] flex items-center justify-between text-[10px] text-[#666] shrink-0">
        <span>Press <kbd className="px-1.5 py-0.5 bg-white border border-[#999] rounded text-[10px] font-mono">⌘B</kbd> anytime to reopen · drag header to move</span>
        <button
          onClick={() => setOpen(false)}
          className="px-3 py-1 text-[11px] font-medium border border-[#999] bg-white hover:bg-[#F5F5F5]"
          data-testid="alarms-close-btn"
        >
          Close
        </button>
      </div>

      {/* Pre-call brief overlay — gold "View Brief" on Crunchy brief reminders */}
      {brief && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setBrief(null)}
          data-testid="alarm-brief-overlay"
        >
          <div
            className="bg-white border border-[#E5C158] shadow-2xl w-full max-w-[600px] rounded-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-2.5 bg-[#FFFBEB] border-b border-[#E5C158] flex items-center justify-between">
              <div className="flex items-center gap-2 text-[13px] font-bold text-[#7A5C00]">
                <Sparkle size={15} weight="fill" className="text-[#F39C12]" />
                Crunchy Pre-Call Brief — {brief.contact_name}
              </div>
              <button
                onClick={() => setBrief(null)}
                className="p-1 text-[#7A5C00] hover:bg-[#F5E9C8] rounded"
                data-testid="alarm-brief-close"
                aria-label="Close brief"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
            <div className="p-4">
              {brief.loading && (
                <div className="flex items-center justify-center gap-2 py-8 text-[12px] text-[#666]" data-testid="alarm-brief-loading">
                  <Spinner size={16} className="animate-spin text-[#F39C12]" />
                  Crunchy is doing the homework…
                </div>
              )}
              {!brief.loading && brief.error && (
                <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="alarm-brief-error">
                  Couldn&apos;t build the brief right now — open the contact card and hit &quot;Brief me&quot; there instead.
                </div>
              )}
              {!brief.loading && brief.data && (
                <div className="grid sm:grid-cols-3 gap-2" data-testid="alarm-brief-content">
                  {[['WHO', brief.data.who], ['HISTORY', brief.data.history], ['OPENER', brief.data.opener]].map(([label, text]) => (
                    text ? (
                      <div key={label} className="bg-[#FFFDF5] border border-[#E5C158]/60 p-2.5">
                        <div className="text-[9px] font-black tracking-widest text-[#B08900] mb-1">{label}</div>
                        <p className="text-[11px] text-[#333] leading-snug">{text}</p>
                      </div>
                    ) : null
                  ))}
                </div>
              )}
            </div>
            <div className="px-4 py-2.5 border-t border-[#EEE] flex items-center justify-end gap-2">
              <button
                onClick={() => setBrief(null)}
                className="px-3 py-1.5 text-[11px] font-medium border border-[#999] bg-white hover:bg-[#F5F5F5]"
                data-testid="alarm-brief-dismiss"
              >
                Close
              </button>
              <button
                onClick={() => {
                  const cid = brief.contact_id;
                  setBrief(null);
                  setOpen(false);
                  navigate(`/contact?id=${cid}`);
                }}
                className="px-3 py-1.5 text-[11px] font-bold bg-[#0B5394] hover:bg-[#094180] text-white"
                data-testid="alarm-brief-open-contact"
              >
                Open Contact Card
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
